import React, { useState, useMemo, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Info, CreditCard, Banknote, Smartphone, Wallet, FileX2, Building2 } from "lucide-react";

/**
 * FINANCIAL CORRECTION ARCHITECTURE - REFUND SAFEGUARDS
 * 
 * This dialog implements safeguards to prevent phantom refund expenses:
 * 1. Display original payment mode prominently
 * 2. Default refund method to original payment mode
 * 3. Warn when selected refund mode differs from original
 * 4. Require explicit confirmation for mismatched refunds
 * 5. NEW: Administrative Void option for wrong shop/billing errors (no cash movement)
 */

// Cancellation types - distinguish between refund scenarios and administrative corrections
const CANCELLATION_TYPES = {
  customer_refund: {
    label: "Customer Refund",
    description: "Customer paid and needs money back",
    icon: Banknote,
  },
  administrative_void: {
    label: "Administrative Void",
    description: "Wrong shop, billing error (no cash movement)",
    icon: FileX2,
  },
};

// Administrative void reasons - predefined for consistency
const ADMIN_VOID_REASONS = [
  { value: "WRONG SHOP SELECTED", label: "Wrong Shop Selected" },
  { value: "WRONG BRANCH SELECTED", label: "Wrong Branch Selected" },
  { value: "BILLING ERROR", label: "Billing Error" },
  { value: "DUPLICATE ENTRY", label: "Duplicate Entry" },
  { value: "TEST TRANSACTION", label: "Test Transaction" },
  { value: "OTHER ADMINISTRATIVE ERROR", label: "Other Administrative Error" },
];

const PAYMENT_MODE_ICONS = {
  cash: Banknote,
  upi: Smartphone,
  card: CreditCard,
  store_credit: Wallet,
  credit: Wallet,
};

const PAYMENT_MODE_LABELS = {
  cash: "CASH",
  upi: "UPI",
  card: "CARD",
  store_credit: "Store Credit",
  credit: "Credit",
};

export function CancelSaleDialog({ sale, onOpenChange, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [refundMethod, setRefundMethod] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [mismatchConfirmed, setMismatchConfirmed] = useState(false);
  const [cancellationType, setCancellationType] = useState("customer_refund");
  const [adminVoidReason, setAdminVoidReason] = useState("");

  const open = !!sale;
  
  // Calculate payment details
  const amountPaid = useMemo(() => Number(sale?.amount_paid || 0), [sale]);
  const saleTotal = useMemo(() => Number(sale?.total || 0), [sale]);
  const hasCustomer = !!sale?.customer_id;
  const needsRefundDecision = amountPaid > 0.01 && hasCustomer;
  const isAdminVoid = cancellationType === "administrative_void";

  // Extract original payment modes from the sale
  const originalPayments = useMemo(() => {
    const payments = sale?.payments || [];
    return payments.filter(p => p && p.mode && p.amount > 0).map(p => ({
      mode: p.mode,
      amount: Number(p.amount || 0)
    }));
  }, [sale]);

  // Primary original payment mode (first/largest payment)
  const primaryOriginalMode = useMemo(() => {
    if (originalPayments.length === 0) return null;
    // Sort by amount descending and get the largest
    const sorted = [...originalPayments].sort((a, b) => b.amount - a.amount);
    return sorted[0].mode;
  }, [originalPayments]);

  // Get all unique original payment modes
  const originalModes = useMemo(() => {
    return [...new Set(originalPayments.map(p => p.mode))];
  }, [originalPayments]);

  // Check if selected refund method is a mismatch
  const isMismatched = useMemo(() => {
    if (!refundMethod || refundMethod === "none" || refundMethod === "store_credit") {
      return false;
    }
    return !originalModes.includes(refundMethod);
  }, [refundMethod, originalModes]);

  // Reset form when dialog opens - default to original payment mode
  useEffect(() => {
    if (open) {
      setReason("");
      setMismatchConfirmed(false);
      setCancellationType("customer_refund"); // Default to customer refund
      setAdminVoidReason("");
      // Default to primary original payment mode
      if (needsRefundDecision && primaryOriginalMode) {
        setRefundMethod(primaryOriginalMode);
      } else {
        setRefundMethod("");
      }
      setRefundAmount(amountPaid > 0 ? amountPaid.toFixed(2) : "");
    }
  }, [open, amountPaid, needsRefundDecision, primaryOriginalMode]);

  // Reset refund method when switching to admin void
  useEffect(() => {
    if (cancellationType === "administrative_void") {
      setRefundMethod("");
      setRefundAmount("");
      setMismatchConfirmed(false);
    } else if (primaryOriginalMode && needsRefundDecision) {
      setRefundMethod(primaryOriginalMode);
      setRefundAmount(amountPaid > 0 ? amountPaid.toFixed(2) : "");
    }
  }, [cancellationType, primaryOriginalMode, needsRefundDecision, amountPaid]);

  // Reset mismatch confirmation when refund method changes
  useEffect(() => {
    setMismatchConfirmed(false);
  }, [refundMethod]);

  if (!open) return null;

  const submit = async () => {
    // For admin void, require admin void reason selection
    if (isAdminVoid) {
      if (!adminVoidReason) {
        return toast.error("Please select an administrative void reason");
      }
      // Use admin void reason as the cancellation reason
      const finalReason = reason.trim() 
        ? `${adminVoidReason}: ${reason.trim()}`
        : adminVoidReason;
      
      setSaving(true);
      try {
        const payload = { 
          reason: finalReason,
          administrative_void: true,
        };
        
        const { data } = await api.post(`/sales/${sale.id}/cancel`, payload);
        if (data.pending_verification) {
          toast.success("Sale marked for administrative void - awaiting owner verification");
        } else {
          toast.success("Sale voided (no drawer impact - administrative correction)");
        }
        onDone?.();
        onOpenChange(false);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSaving(false);
      }
      return;
    }
    
    // Regular customer refund flow
    if (!reason.trim()) return toast.error("Reason is required");
    
    // Validate refund method for paid sales with customer (only for non-admin-void)
    if (needsRefundDecision && !refundMethod) {
      return toast.error("Please select a refund method");
    }

    // Require confirmation for mismatched refunds
    if (isMismatched && !mismatchConfirmed) {
      return toast.error("Please confirm the refund method mismatch by checking the confirmation box");
    }
    
    const refundAmt = parseFloat(refundAmount) || 0;
    if (needsRefundDecision && refundMethod !== "none" && refundAmt > amountPaid) {
      return toast.error(`Refund amount cannot exceed amount paid (₹${amountPaid.toFixed(2)})`);
    }
    
    setSaving(true);
    try {
      const payload = { 
        reason: reason.trim(),
      };
      
      // Add refund info if sale has payments and customer
      if (needsRefundDecision) {
        payload.refund_method = refundMethod;
        payload.refund_amount = refundMethod === "none" ? 0 : refundAmt;
        // Log mismatch for audit
        if (isMismatched) {
          payload.refund_mismatch_confirmed = true;
          payload.original_payment_modes = originalModes;
        }
      }
      
      const { data } = await api.post(`/sales/${sale.id}/cancel`, payload);
      if (data.pending_verification) {
        toast.success("Sale marked for deletion - awaiting owner verification");
      } else {
        const refundMsg = needsRefundDecision && refundMethod !== "none" 
          ? ` · Refund: ₹${refundAmt.toFixed(2)} (${refundMethod.toUpperCase()})`
          : "";
        toast.success(`Sale canceled and stock restored${refundMsg}`);
      }
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const PaymentIcon = primaryOriginalMode ? PAYMENT_MODE_ICONS[primaryOriginalMode] || Wallet : Wallet;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="sale-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-red-700">Cancel sale</DialogTitle>
          <DialogDescription>
            This will permanently mark the sale as canceled and restore stock.
            {needsRefundDecision && " Please specify how to handle the refund."}
          </DialogDescription>
        </DialogHeader>
        
        {/* Sale Summary */}
        <div className="rounded-md bg-[hsl(var(--muted))] p-3 text-sm">
          <div className="flex justify-between">
            <span>Total</span>
            <span className="font-mono-data font-semibold">₹{saleTotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="text-muted-foreground">Paid so far</span>
            <span className="font-mono-data">₹{amountPaid.toFixed(2)}</span>
          </div>
          {sale?.customer_name && (
            <div className="flex justify-between text-xs mt-1">
              <span className="text-muted-foreground">Customer</span>
              <span>{sale.customer_name}</span>
            </div>
          )}
        </div>

        {/* ORIGINAL PAYMENT MODE DISPLAY - PROMINENT */}
        {needsRefundDecision && originalPayments.length > 0 && !isAdminVoid && (
          <div 
            className="p-3 bg-blue-50 border border-blue-200 rounded-md"
            data-testid="original-payment-display"
          >
            <div className="flex items-center gap-2 text-sm font-medium text-blue-900 mb-2">
              <Info className="w-4 h-4" />
              Original Payment Method
            </div>
            <div className="space-y-1">
              {originalPayments.map((payment, idx) => {
                const Icon = PAYMENT_MODE_ICONS[payment.mode] || Wallet;
                return (
                  <div key={idx} className="flex items-center gap-2 text-blue-800">
                    <Icon className="w-4 h-4" />
                    <span className="font-semibold">{PAYMENT_MODE_LABELS[payment.mode] || payment.mode.toUpperCase()}</span>
                    <span className="font-mono-data">₹{payment.amount.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CANCELLATION TYPE SELECTOR - For paid sales */}
        {needsRefundDecision && (
          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Cancellation Type *
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setCancellationType("customer_refund")}
                className={`p-3 rounded-md border-2 text-left transition-all ${
                  cancellationType === "customer_refund"
                    ? "border-orange-500 bg-orange-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid="cancel-type-refund"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Banknote className="w-4 h-4 text-orange-600" />
                  <span className="font-medium text-sm">Customer Refund</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Customer paid, needs money back
                </p>
              </button>
              
              <button
                type="button"
                onClick={() => setCancellationType("administrative_void")}
                className={`p-3 rounded-md border-2 text-left transition-all ${
                  cancellationType === "administrative_void"
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid="cancel-type-admin-void"
              >
                <div className="flex items-center gap-2 mb-1">
                  <FileX2 className="w-4 h-4 text-purple-600" />
                  <span className="font-medium text-sm">Admin Void</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Wrong shop / billing error
                </p>
              </button>
            </div>
          </div>
        )}

        {/* ADMINISTRATIVE VOID SECTION */}
        {isAdminVoid && needsRefundDecision && (
          <div className="space-y-3 p-3 border-2 border-purple-300 rounded-md bg-purple-50">
            <div className="flex items-center gap-2 text-sm font-medium text-purple-800">
              <Building2 className="w-4 h-4" />
              Administrative Void
            </div>
            
            <div className="p-2 bg-purple-100/50 rounded text-xs text-purple-700">
              <strong>No cash movement will be recorded.</strong>
              <br />
              Use this when the sale was created by mistake in the wrong shop
              or due to a billing error. The drawer will not be affected.
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Void Reason *
              </Label>
              <Select value={adminVoidReason} onValueChange={setAdminVoidReason}>
                <SelectTrigger data-testid="admin-void-reason-select">
                  <SelectValue placeholder="Select reason for void" />
                </SelectTrigger>
                <SelectContent>
                  {ADMIN_VOID_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Additional Notes (Optional)
              </Label>
              <Textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Sale was meant for VJD shop"
                data-testid="admin-void-notes"
              />
            </div>
          </div>
        )}
        
        {/* Refund Options - Only show if sale has payments AND customer AND not admin void */}
        {needsRefundDecision && !isAdminVoid && (
          <div className="space-y-3 p-3 border border-orange-200 rounded-md bg-orange-50/50">
            <div className="flex items-center gap-2 text-sm font-medium text-orange-800">
              <AlertTriangle className="w-4 h-4" />
              Refund Options
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Refund Method *
              </Label>
              <Select value={refundMethod} onValueChange={setRefundMethod}>
                <SelectTrigger data-testid="refund-method-select">
                  <SelectValue placeholder="Select refund method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="store_credit">Store Credit</SelectItem>
                  <SelectItem value="none">No Refund</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* MISMATCH WARNING */}
            {isMismatched && (
              <div 
                className="p-3 bg-amber-50 border-2 border-amber-400 rounded-md"
                data-testid="refund-mismatch-warning"
              >
                <div className="flex items-center gap-2 text-amber-800 font-bold mb-2">
                  <AlertTriangle className="w-5 h-5" />
                  PAYMENT MODE MISMATCH
                </div>
                <p className="text-amber-700 text-sm">
                  Customer originally paid via <strong>{PAYMENT_MODE_LABELS[primaryOriginalMode] || primaryOriginalMode?.toUpperCase()}</strong>.
                  You selected <strong>{PAYMENT_MODE_LABELS[refundMethod] || refundMethod?.toUpperCase()}</strong> refund.
                </p>
                <p className="text-amber-600 text-xs mt-2">
                  Only choose <strong>CASH</strong> if you are physically returning 
                  cash to the customer right now. Selecting the wrong refund method 
                  will cause a cash drawer discrepancy.
                </p>
                
                {/* REQUIRED CONFIRMATION */}
                <div className="mt-3 flex items-start gap-2">
                  <Checkbox
                    id="mismatch-confirm"
                    checked={mismatchConfirmed}
                    onCheckedChange={setMismatchConfirmed}
                    data-testid="mismatch-confirm-checkbox"
                  />
                  <label 
                    htmlFor="mismatch-confirm" 
                    className="text-sm text-amber-800 cursor-pointer leading-tight"
                  >
                    I confirm this refund method is intentional and I understand the cash drawer impact
                  </label>
                </div>
              </div>
            )}
            
            {refundMethod && refundMethod !== "none" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Refund Amount
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={amountPaid}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder={`Max: ₹${amountPaid.toFixed(2)}`}
                  className="font-mono-data"
                  data-testid="refund-amount-input"
                />
                <p className="text-xs text-muted-foreground">
                  {refundMethod === "store_credit" 
                    ? "Amount will be added to customer's store credit balance"
                    : `Amount will be refunded via ${refundMethod.toUpperCase()} and deducted from shop`
                  }
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Reason - Only show for non-admin-void (admin void has its own reason field) */}
        {!isAdminVoid && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reason *</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Customer refused, duplicate entry, test sale"
              className="mt-1"
              data-testid="sale-cancel-reason"
            />
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Back
          </Button>
          <Button
            variant={isAdminVoid ? "default" : "destructive"}
            onClick={submit}
            disabled={saving || (!isAdminVoid && isMismatched && !mismatchConfirmed)}
            className={isAdminVoid ? "bg-purple-600 hover:bg-purple-700" : ""}
            data-testid="sale-cancel-confirm"
          >
            {saving ? "Processing…" : isAdminVoid ? "Void Sale" : "Cancel sale"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
